import { useState, useEffect } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { formatRut, formatDate, formatCurrency, getOrderStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ShoppingCart,
  Plus,
  Search,
  Loader2,
  Trash2,
  Building2,
  MoreHorizontal,
  CheckCircle,
  Send,
  Package,
  XCircle,
} from 'lucide-react';

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    supplier_id: '',
    warehouse_id: '',
    items: [],
    notes: '',
  });

  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    quantity: 1,
    unit_price: 0,
  });

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, productsRes, warehousesRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/suppliers'),
        api.get('/products'),
        api.get('/warehouses'),
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    return order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
           order.supplier_name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const selectedSupplier = suppliers.find(s => s.supplier_id === formData.supplier_id);

  const addItem = () => {
    if (!currentItem.product_id || currentItem.quantity < 1 || currentItem.unit_price < 0) {
      toast.error('Complete los datos del item');
      return;
    }

    const product = products.find(p => p.product_id === currentItem.product_id);
    if (!product) return;

    const existing = formData.items.find(i => i.product_id === currentItem.product_id);
    if (existing) {
      toast.error('Este producto ya está en la orden');
      return;
    }

    const total = currentItem.quantity * currentItem.unit_price;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: product.product_id,
        product_name: product.name,
        quantity: currentItem.quantity,
        unit_price: currentItem.unit_price,
        total,
      }]
    }));

    setCurrentItem({ product_id: '', quantity: 1, unit_price: 0 });
  };

  const removeItem = (productId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.product_id !== productId)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((acc, item) => acc + item.total, 0);
    const tax = Math.round(subtotal * 0.19);
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.supplier_id || !formData.warehouse_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }

    const { subtotal, tax, total } = calculateTotals();

    setSubmitting(true);
    try {
      const orderData = {
        ...formData,
        supplier_name: selectedSupplier?.business_name || '',
        supplier_rut: selectedSupplier?.rut || '',
        subtotal,
        tax,
        total,
      };

      await api.post('/purchase-orders', orderData);
      toast.success('Orden de compra creada exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear orden'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/purchase-orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Estado actualizado a ${getOrderStatusLabel(newStatus)}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      warehouse_id: '',
      items: [],
      notes: '',
    });
    setCurrentItem({ product_id: '', quantity: 1, unit_price: 0 });
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: 'bg-slate-100 text-slate-700',
      approved: 'bg-blue-100 text-blue-700',
      sent: 'bg-amber-100 text-amber-700',
      received: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return <Badge className={`${classes[status]} hover:${classes[status]}`}>{getOrderStatusLabel(status)}</Badge>;
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Órdenes de Compra
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de órdenes de compra a proveedores
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="add-order-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Orden
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Nueva Orden de Compra</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor *</Label>
                  <Select 
                    value={formData.supplier_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}
                  >
                    <SelectTrigger className="bg-slate-50" data-testid="order-supplier">
                      <SelectValue placeholder="Seleccionar proveedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(sup => (
                        <SelectItem key={sup.supplier_id} value={sup.supplier_id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span>{sup.business_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bodega Destino *</Label>
                  <Select 
                    value={formData.warehouse_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, warehouse_id: v }))}
                  >
                    <SelectTrigger className="bg-slate-50" data-testid="order-warehouse">
                      <SelectValue placeholder="Seleccionar bodega" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map(wh => (
                        <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                          {wh.name} {wh.is_main && '(Principal)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                <Label>Productos *</Label>
                <div className="flex gap-2 flex-wrap">
                  <Select 
                    value={currentItem.product_id} 
                    onValueChange={(v) => setCurrentItem(prev => ({ ...prev, product_id: v }))}
                  >
                    <SelectTrigger className="flex-1 min-w-[200px] bg-slate-50" data-testid="order-product">
                      <SelectValue placeholder="Producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(prod => (
                        <SelectItem key={prod.product_id} value={prod.product_id}>
                          {prod.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-24 bg-slate-50"
                    placeholder="Cant."
                    data-testid="order-quantity"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={currentItem.unit_price}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, unit_price: parseInt(e.target.value) || 0 }))}
                    className="w-32 bg-slate-50"
                    placeholder="Precio unit."
                    data-testid="order-price"
                  />
                  <Button type="button" onClick={addItem} variant="outline" data-testid="add-order-item">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead>Cantidad</TableHead>
                          <TableHead>Precio Unit.</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(item.total)}</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(item.product_id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="p-4 bg-slate-50 border-t space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal</span>
                        <span>{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>IVA (19%)</span>
                        <span>{formatCurrency(totals.tax)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="submit-order-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Orden'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por número o proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-50"
              data-testid="search-orders"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <ShoppingCart className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay órdenes de compra</p>
              <p className="text-sm">Cree la primera orden para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N° Orden</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="hidden md:table-cell">Fecha</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.order_id} className="table-row-hover">
                      <TableCell className="font-mono">{order.order_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{order.supplier_name}</p>
                          <p className="text-xs text-slate-500">{formatRut(order.supplier_rut)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatDate(order.created_at)}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(order.total)}</TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`order-actions-${order.order_id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {order.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'approved')}>
                                <CheckCircle className="w-4 h-4 mr-2 text-blue-500" />
                                Aprobar
                              </DropdownMenuItem>
                            )}
                            {order.status === 'approved' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'sent')}>
                                <Send className="w-4 h-4 mr-2 text-amber-500" />
                                Marcar como Enviada
                              </DropdownMenuItem>
                            )}
                            {order.status === 'sent' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'received')}>
                                <Package className="w-4 h-4 mr-2 text-emerald-500" />
                                Recepcionar
                              </DropdownMenuItem>
                            )}
                            {order.status !== 'received' && order.status !== 'cancelled' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'cancelled')}>
                                <XCircle className="w-4 h-4 mr-2 text-red-500" />
                                Cancelar
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseOrders;
